import { create } from 'zustand';
import type { ModalAssetItem } from '~/features/chat/modal-asset';
import { findWindow } from '~/lib/stores/window-rules';
import { useWindowStore } from '~/lib/stores/window-store';
import { DEFAULT_SETTINGS_SECTION, type SettingsSectionId } from '~/lib/settings-sections';
import { DEFAULT_SYSTEM_SETTINGS_SECTION, type SystemSettingsSectionId } from '~/lib/system-settings-sections';
import { SETTINGS_WINDOW_ID, SYSTEM_SETTINGS_WINDOW_ID, appWindowId, assetWindowTitle, buildWindowId, chatWindowId, fileWindowId, fileWindowTitle, uploadWindowId } from './window-titles';
import type { FileSource } from './file-view';
import { isUiRefactoringOn } from '~/lib/ui-refactoring';
import { useLegacyOpenStore } from './legacy-open-store';
import { settingsPath } from '~/lib/settings-sections';
import { systemSettingsPath } from '~/lib/system-settings-sections';

/**
 * FRONT-3223: with `eddy.ui-refactoring` OFF there is no window layer. Every
 * opener below then hands its request to `legacy-open-store.ts`, which
 * `LegacyWindowHost` renders as the one-at-a-time overlays of before, and
 * Settings becomes the `/settings` route modal again. The window store stays
 * empty, so the top bar shows no tabs. Checked per call, never cached: the
 * flag is seeded before the shell renders and does not change mid-session.
 */
function windowsOff(): boolean {
  return !isUiRefactoringOn();
}

/**
 * FRONT-3146: what a HOST-owned window needs to render beyond the window
 * model's own fields (which stay serializable and rule-pure). Keyed by window
 * id; `WindowHost` renders one surface per window that has a payload here.
 */
export interface ChatWindowPayload {
  item: ModalAssetItem;
  /** The page the request was raised from; see `shouldReturnToOrigin`. */
  returnTo: string | null;
}

export interface BuildWindowPayload {
  /** `undefined`: resume the most recent build, or start one. */
  sessionId: string | undefined;
  returnTo: string | null;
  /** Start straight on the new-build step (the top bar's Build button). */
  fresh?: boolean;
}

export interface SettingsWindowPayload {
  /** The rail's active section — window state, never the address. */
  section: SettingsSectionId;
}

export interface SystemSettingsWindowPayload {
  section: SystemSettingsSectionId;
  /**
   * The extra query a link asked for (`tab=live`, `provider=github`), handed
   * to `section`'s panel as its own search params — the window has no address
   * of its own, and the page behind it must not have its URL rewritten.
   */
  query: string;
}

/** FRONT-3171: a file shown on its own — a Build project's, or an uploaded one. */
export interface FileWindowPayload {
  source: FileSource;
}

/** An app's Preview, live in a window beside the Build that made it. */
export interface AppWindowPayload {
  slug: string;
  /** The app's own address — what "Open in a new tab" opens. */
  url: string;
}

interface PayloadState {
  chat: Record<string, ChatWindowPayload>;
  build: Record<string, BuildWindowPayload>;
  file: Record<string, FileWindowPayload>;
  app: Record<string, AppWindowPayload>;
  /** There is one Settings window; null while it is not open. */
  settings: SettingsWindowPayload | null;
  systemSettings: SystemSettingsWindowPayload | null;
}

export const useWindowPayloads = create<PayloadState>(() => ({
  chat: {},
  build: {},
  file: {},
  app: {},
  settings: null,
  systemSettings: null,
}));

/** Drops payloads whose window is gone (closed, cancelled in the chooser). */
export function pruneWindowPayloads(): void {
  const model = useWindowStore.getState();
  const { chat, build, file, app, settings, systemSettings } = useWindowPayloads.getState();
  const keep = <T,>(rec: Record<string, T>) => {
    const ids = Object.keys(rec).filter((id) => findWindow(model, id));
    return ids.length === Object.keys(rec).length ? rec : Object.fromEntries(ids.map((id) => [id, rec[id]]));
  };
  const nextChat = keep(chat);
  const nextBuild = keep(build);
  const nextFile = keep(file);
  const nextApp = keep(app);
  const nextSettings = settings && findWindow(model, SETTINGS_WINDOW_ID) ? settings : null;
  const nextSystem = systemSettings && findWindow(model, SYSTEM_SETTINGS_WINDOW_ID) ? systemSettings : null;
  if (
    nextChat !== chat ||
    nextBuild !== build ||
    nextFile !== file ||
    nextApp !== app ||
    nextSettings !== settings ||
    nextSystem !== systemSettings
  ) {
    useWindowPayloads.setState({
      chat: nextChat,
      build: nextBuild,
      file: nextFile,
      app: nextApp,
      settings: nextSettings,
      systemSettings: nextSystem,
    });
  }
}

/**
 * Open (or focus) a chat / document / file window. An asset that already has a
 * window is focused, never duplicated, and keeps the payload it opened with.
 */
export function openChatAssetWindow(item: ModalAssetItem, returnTo: string | null = null): void {
  if (windowsOff()) {
    useLegacyOpenStore.getState().openChat(item, returnTo);
    return;
  }
  const id = chatWindowId(item);
  if (!findWindow(useWindowStore.getState(), id)) {
    useWindowPayloads.setState((s) => ({ chat: { ...s.chat, [id]: { item, returnTo } } }));
  }
  useWindowStore.getState().open({ id, kind: 'chat', ref: item.id, title: assetWindowTitle(item) });
}

export function openBuildWindow(
  sessionId: string | null | undefined,
  returnTo: string | null = null,
  fresh = false,
): void {
  if (windowsOff()) {
    // The old window resumes the latest build; `fresh` has no meaning there.
    useLegacyOpenStore.getState().openBuild(sessionId ?? undefined, returnTo);
    return;
  }
  // A fresh build gets its own window every time (eddy, 2026-10-26: "Build
  // always creates a new build"), so several new builds can be open at once.
  const id = fresh ? `build:new-${Date.now().toString(36)}` : buildWindowId(sessionId);
  if (fresh) lastFreshBuildId = id;
  if (!findWindow(useWindowStore.getState(), id)) {
    useWindowPayloads.setState((s) => ({
      build: { ...s.build, [id]: { sessionId: sessionId ?? undefined, returnTo, ...(fresh ? { fresh: true } : {}) } },
    }));
  }
  useWindowStore.getState().open({ id, kind: 'build', ref: sessionId ?? null, title: fresh ? 'New build' : 'Build' });
}

/**
 * FRONT-3171: open a file the Builder wrote, in a window of its own — beside
 * the Build it came from, like Settings. The same file again focuses its window.
 */
export function openFileWindow(sessionId: string, path: string, besideId?: string | null): void {
  const id = fileWindowId(sessionId, path);
  if (!findWindow(useWindowStore.getState(), id)) {
    useWindowPayloads.setState((s) => ({ file: { ...s.file, [id]: { source: { kind: 'project', sessionId, path } } } }));
  }
  useWindowStore.getState().openBeside({ id, kind: 'file', ref: sessionId, title: fileWindowTitle(path) }, besideId);
}

/**
 * A file attached to a message (eddy, 2026-10-26: "let me click it to open in
 * a new window to the right") — the same file window, beside the chat or
 * Build it was clicked in. The same file again focuses its window.
 */
export function openUploadWindow(file: { fileId: string; name: string; contentType?: string }, besideId?: string | null): void {
  const id = uploadWindowId(file.fileId);
  if (!findWindow(useWindowStore.getState(), id)) {
    useWindowPayloads.setState((s) => ({ file: { ...s.file, [id]: { source: { kind: 'upload', ...file } } } }));
  }
  useWindowStore.getState().openBeside({ id, kind: 'file', ref: file.fileId, title: fileWindowTitle(file.name) }, besideId);
}

/**
 * eddy, 2026-10-26: "App Preview should open in a window in the main bar, right
 * of the one we are using to open it". The app runs live in that window, beside
 * the Build (or chat) it was opened from. The same app again focuses its window.
 */
/**
 * FRONT-3273: the address the app window shows and copies, always a whole one.
 * Pro Builder knows an app only by its path (`/apps/<slug>/`); a copied bare
 * path pasted elsewhere goes nowhere, so it is completed against this page's
 * own origin, which serves the app. An address that is already whole stays as
 * it is; with no origin to hand (no page) the input is kept.
 */
export function absoluteAppUrl(url: string, origin: string | undefined = globalThis.location?.origin): string {
  if (!origin || /^[a-z][a-z\d+.-]*:/i.test(url)) return url;
  try {
    return new URL(url, origin).href;
  } catch {
    return url;
  }
}

export function openAppWindow(app: { slug: string; url: string; title: string }, besideId?: string | null): void {
  const id = appWindowId(app.slug);
  useWindowPayloads.setState((s) => ({ app: { ...s.app, [id]: { slug: app.slug, url: absoluteAppUrl(app.url) } } }));
  useWindowStore.getState().openBeside({ id, kind: 'app', ref: app.slug, title: app.title }, besideId);
}

let lastFreshBuildId: string | null = null;

/** The window the top bar's Build button opened last — its second click hides that one. */
export function lastFreshBuildWindowId(): string | null {
  return lastFreshBuildId;
}

/**
 * A Build window picked a project — from the new-build screen's list, or by
 * creating one. A project is shown in ONE window (eddy, 2026-10-26: "when I
 * click new Builder and select a project that is already open, it opens a
 * second time — just show the one and close the new"):
 * - already open elsewhere → that window comes forward, this one closes
 * - otherwise → this window BECOMES the project's window (its id is now the
 *   project's), so any later "open project X" finds it instead of a copy
 */
export function claimBuildWindow(fromId: string, sessionId: string, title = 'Build'): 'same' | 'existing' | 'claimed' {
  const toId = buildWindowId(sessionId);
  if (toId === fromId) return 'same';
  const store = useWindowStore.getState();
  if (findWindow(store, toId)) {
    // Close first: it frees the column, so the restore never asks which window to replace.
    store.close(fromId);
    useWindowStore.getState().restore(toId);
    return 'existing';
  }
  const payload = useWindowPayloads.getState().build[fromId];
  useWindowPayloads.setState((s) => {
    const next = { ...s.build };
    delete next[fromId];
    next[toId] = { sessionId, returnTo: payload?.returnTo ?? null };
    return { build: next };
  });
  store.retarget(fromId, { id: toId, ref: sessionId, title });
  if (lastFreshBuildId === fromId) lastFreshBuildId = toId;
  return 'claimed';
}

/** "+" inside a hosted chat window: the same slot now shows a fresh chat. */
export function retargetChatWindow(fromId: string, item: ModalAssetItem): void {
  const toId = chatWindowId(item);
  const payload = useWindowPayloads.getState().chat[fromId];
  useWindowPayloads.setState((s) => {
    const next = { ...s.chat };
    delete next[fromId];
    next[toId] = { item, returnTo: payload?.returnTo ?? null };
    return { chat: next };
  });
  useWindowStore.getState().retarget(fromId, { id: toId, ref: item.id, title: assetWindowTitle(item) });
}

/**
 * eddy, 2026-10-26: "a second click in the main bar should hide the window
 * again". The top bar's doors (a question chip, Build) call this first: if
 * their window is already shown it is minimized and the caller stops; else
 * the caller opens it as usual. Returns whether it minimized.
 */
export function minimizeIfShown(id: string): boolean {
  const s = useWindowStore.getState();
  const w = findWindow(s, id);
  if (!w || w.state !== 'visible') return false;
  s.minimize(id);
  return true;
}

/**
 * Open Settings as a WINDOW docked beside another one (FRONT-3146) — always,
 * also with no other window around (then it simply opens on its own): the window
 * `besideId` (default: the focused one) keeps the left column, Settings takes
 * the right, every other visible window is minimized (`openBeside` in
 * `window-rules.ts`). Opening again with a section switches the rail to it.
 */
export function openSettingsWindow(section?: SettingsSectionId, besideId?: string | null): void {
  if (windowsOff()) {
    useLegacyOpenStore.getState().requestNavigation(section ? settingsPath(section) : '/settings');
    return;
  }
  useWindowPayloads.setState((s) => ({
    settings: { section: section ?? s.settings?.section ?? DEFAULT_SETTINGS_SECTION },
  }));
  useWindowStore.getState().openBeside({ id: SETTINGS_WINDOW_ID, kind: 'settings', title: 'Settings' }, besideId);
}

/** The Settings window's rail: switch section without touching the address. */
export function setSettingsWindowSection(section: SettingsSectionId): void {
  if (windowsOff()) {
    useLegacyOpenStore.getState().requestNavigation(settingsPath(section));
    return;
  }
  useWindowPayloads.setState({ settings: { section } });
}

/**
 * System Settings as a window, docked the same way as User Settings. Every
 * door into it — the avatar menu, the command palette, a `/system-settings`
 * link anywhere in the app — ends here, so it is never a modal any more.
 */
export function openSystemSettingsWindow(section?: SystemSettingsSectionId, query = '', besideId?: string | null): void {
  if (windowsOff()) {
    const base = section ? systemSettingsPath(section) : '/system-settings';
    useLegacyOpenStore.getState().requestNavigation(query ? `${base}${base.includes('?') ? '&' : '?'}${query}` : base);
    return;
  }
  useWindowPayloads.setState((s) => ({
    systemSettings: {
      section: section ?? s.systemSettings?.section ?? DEFAULT_SYSTEM_SETTINGS_SECTION,
      query,
    },
  }));
  useWindowStore
    .getState()
    .openBeside({ id: SYSTEM_SETTINGS_WINDOW_ID, kind: 'system-settings', title: 'System Settings' }, besideId);
}

/** The System Settings window's rail. A requested extra query belonged to the old section, so it goes. */
export function setSystemSettingsWindowSection(section: SystemSettingsSectionId): void {
  useWindowPayloads.setState({ systemSettings: { section, query: '' } });
}
